<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">Mi perfil</h2>
        <p class="db-page-subtitle">Tus datos de acceso al panel.</p>
      </div>
    </header>

    <div v-if="usuario" class="vp-columns">
      <section class="db-card">
        <div class="db-card-head">
          <h3 class="db-card-title"><span class="material-symbols-outlined">person</span> Datos personales</h3>
        </div>
        <form class="db-form db-card-body" novalidate @submit.prevent="guardarDatos">
          <div class="db-field">
            <label class="db-label" for="vp-nombre">Nombre</label>
            <input id="vp-nombre" v-model.trim="nombre" type="text" class="db-input" maxlength="100" autocomplete="name" />
          </div>
          <div class="db-field">
            <label class="db-label" for="vp-email">Correo electrónico</label>
            <input id="vp-email" v-model.trim="email" type="email" class="db-input" maxlength="191" autocomplete="email" />
          </div>
          <div v-if="cambiaEmail" class="db-field">
            <label class="db-label" for="vp-pass">Contraseña actual</label>
            <input id="vp-pass" v-model="passwordActual" type="password" class="db-input" autocomplete="current-password" />
            <p class="db-hint">Por seguridad, cambiar el correo de acceso requiere tu contraseña.</p>
          </div>
          <div class="vp-actions">
            <button type="submit" class="db-btn" :disabled="!hayCambios || guardando">
              <span v-if="guardando" class="db-spinner"></span>
              Guardar datos
            </button>
          </div>
        </form>
      </section>

      <section class="db-card">
        <div class="db-card-head">
          <h3 class="db-card-title"><span class="material-symbols-outlined">badge</span> Rol y acceso</h3>
        </div>
        <div class="db-card-body vp-info">
          <div>
            <span class="db-hint">Rol</span>
            <span class="db-badge" :class="usuario.es_superadmin ? 'db-badge--gold' : 'db-badge--info'">{{ usuario.rol_etiqueta }}</span>
          </div>
          <div>
            <span class="db-hint">Puedes gestionar</span>
            <ul class="vp-permisos">
              <li v-for="p in permisosLegibles" :key="p" class="db-badge">{{ p }}</li>
              <li v-if="!permisosLegibles.length" class="db-hint">Ninguna sección.</li>
            </ul>
          </div>
          <div>
            <span class="db-hint">Último acceso</span>
            <strong>{{ formatFechaHora(usuario.ultimo_acceso) }}</strong>
          </div>
          <p class="db-hint">El rol solo lo puede cambiar un administrador.</p>
        </div>
      </section>

      <section class="db-card vp-full">
        <div class="db-card-head">
          <h3 class="db-card-title"><span class="material-symbols-outlined">key</span> Cambiar contraseña</h3>
        </div>
        <div class="db-card-body">
          <p class="db-hint vp-note">Al cambiarla se cerrarán tus sesiones abiertas en otros dispositivos.</p>
          <form-cambio-password @cambiada="alCambiarPassword" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "@/directives/axios.js";
import FormCambioPassword from "@/components/dashboard/usuarios/FormCambioPassword.vue";
import { avisar, formatFechaHora, PERMISOS, avisarError } from "@/directives/dbDialogs.js";

const NOMBRES = {
  [PERMISOS.NOTICIAS]: "Noticias",
  [PERMISOS.CARITAS]: "Noticias de Cáritas",
  [PERMISOS.GALERIAS]: "Galerías",
  [PERMISOS.POPUPS]: "Avisos emergentes",
  [PERMISOS.ENLACES]: "Sitios de interés",
  [PERMISOS.HORARIOS]: "Horarios",
  [PERMISOS.USUARIOS]: "Usuarios",
  [PERMISOS.ROLES]: "Roles",
};

export default {
  name: "VistaPerfil",

  components: { FormCambioPassword },

  data() {
    return {
      nombre: "",
      email: "",
      passwordActual: "",
      guardando: false,
    };
  },

  computed: {
    usuario() {
      return this.$store.getters.usuario;
    },
    cambiaEmail() {
      return Boolean(this.usuario) && this.email.toLowerCase() !== (this.usuario.email || "").toLowerCase();
    },
    hayCambios() {
      return Boolean(this.usuario) && (this.cambiaEmail || this.nombre !== this.usuario.name);
    },
    permisosLegibles() {
      return ((this.usuario && this.usuario.permisos) || []).map((p) => NOMBRES[p] || p);
    },
  },

  watch: {
    usuario: {
      immediate: true,
      handler(u) {
        if (!u) return;
        this.nombre = u.name || "";
        this.email = u.email || "";
        this.passwordActual = "";
      },
    },
  },

  methods: {
    formatFechaHora,

    alCambiarPassword() {
      avisar(this, "Contraseña actualizada.");
    },

    async guardarDatos() {
      if (!this.nombre || !this.email) {
        avisar(this, "Nombre y correo son obligatorios.", "warning");
        return;
      }
      if (this.cambiaEmail && !this.passwordActual) {
        avisar(this, "Escribe tu contraseña actual para cambiar el correo.", "warning");
        return;
      }

      this.guardando = true;
      try {
        const response = await axios.put("/perfil", {
          name: this.nombre,
          email: this.email,
          current_password: this.cambiaEmail ? this.passwordActual : null,
        });
        this.$store.commit("setUsuario", response.data.usuario);
        avisar(this, response.data.message || "Datos guardados.");
      } catch (error) {
        avisarError(this, error, "No se pudieron guardar tus datos.");
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>

<style scoped>
.vp-columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.vp-full {
  grid-column: 1 / -1;
}

.vp-actions {
  display: flex;
  justify-content: flex-end;
}

.vp-info {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.vp-info > div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.vp-permisos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.vp-note {
  margin: 0 0 12px;
}
</style>
